import React, { createContext, useContext, useState } from "react";

import { Alert, AlertTitle, Snackbar } from "@mui/material";
import Box from "@mui/material/Box";
import { fontFamily, maxHeight, maxWidth } from "@mui/system";

export type SnackbarParams = {
  title: string;
  message?: string;
  severity?: "info" | "warning" | "error" | "success";
};

export const SnackbarContext = createContext<(data: SnackbarParams) => void>(
  () => {}
);

export type ShowSnackbarFunc = (params: SnackbarParams) => void;

export function useSnackbar(): ShowSnackbarFunc {
  return useContext(SnackbarContext);
}

export default function SnackbarProvider({
  children,
  autoHideDuration = 5000,
}) {
  const [data, setData] = useState(null);

  const showSnackbar = (snackData: SnackbarParams) => setData(snackData);

  return (
    <SnackbarContext.Provider value={showSnackbar}>
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        open={!!data}
        onClose={() => {
          setData(null);
        }}
        autoHideDuration={
          data?.severity == "error" ? undefined : autoHideDuration
        }
        sx={{
          userSelect: "all",
          maxWidth: "1200px",
        }}
      >
        {data && (
          <Alert severity={data.severity || "info"} sx={{ marginLeft: 2 }}>
            {data.title && <AlertTitle>{data.title}</AlertTitle>}
            {Boolean(data.message) ? (
              <Box
                component="pre"
                sx={{
                  padding: 0,
                  margin: 0,
                  overflow: "auto",
                  maxHeight: "90vh",
                  textWrap: "wrap",
                  fontFamily: "var(--font-family)",
                }}
              >
                {data.message}
              </Box>
            ) : null}
          </Alert>
        )}
      </Snackbar>

      {children}
    </SnackbarContext.Provider>
  );
}
